import { Fragment, useState } from "react";
import {
  useListEcoleMotifs,
  useCreateEcoleMotif,
  useUpdateEcoleMotif,
  useDeleteEcoleMotif,
  getListEcoleMotifsQueryKey,
} from "@/lib/backoffice-hooks";
import { useAnneeScope } from "@/lib/annee-scope";
import { useQueryClient } from "@tanstack/react-query";
import EcoleShell from "@/components/layout/EcoleShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import CsvImportCard from "@/components/CsvImportCard";
import AnneeInactiveBanner from "@/components/AnneeInactiveBanner";
import { importBaremesCsv, type EcoleMotif } from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import { Plus, Loader2, Pencil, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import MotifBaremesPanel from "@/components/MotifBaremesPanel";
import { useMotifBaremesEcole } from "@/lib/backoffice-hooks";

const CATEGORIES = [
  "SCOLARITE", "INSCRIPTION", "CANTINE", "TRANSPORT", "UNIFORME",
  "LIVRES", "SORTIE", "EXAMEN", "MEDICAL", "APE", "AUTRE",
] as const;

const FREQUENCES = ["", "T1", "T2", "T3", "ANNUEL", "MENSUEL", "INSCRIPTION"] as const;

/**
 * Le motif dit QUOI (libellé, catégorie, compte, période) — jamais combien.
 * L'argent vit dans la grille des tarifs par niveau/classe (MotifBaremesPanel).
 */
interface EditMotifState {
  id: string;
  libelle: string;
  categorie: string;
  frequence: string;
  compteSyscohada: string;
  /** "true" = dû par tous ; "false" = facultatif, activé élève par élève. */
  obligatoire: "true" | "false";
}

function fcfa(n: number) {
  return new Intl.NumberFormat("fr-FR").format(n) + " FCFA";
}

export default function MotifsPage() {
  const qc = useQueryClient();
  const { isActiveAnnee } = useAnneeScope();
  // Écran de GESTION → tous les motifs (actifs + inactifs) pour pouvoir réactiver
  // un motif désactivé. Les dropdowns de SAISIE restent actifs uniquement.
  const { data: motifs, isLoading } = useListEcoleMotifs({ includeInactifs: true });
  const updateMutation = useUpdateEcoleMotif();
  const [openCreate, setOpenCreate] = useState(false);
  // Motif déplié sur sa grille tarifaire.
  const [baremesOuverts, setBaremesOuverts] = useState<string | null>(null);
  // Résumé des tarifs réels par motif : chaque classe a son prix (260 000 en
  // CP, 317 000 en CM…), on affiche donc la fourchette réelle, jamais un forfait.
  const { data: tousBaremes } = useMotifBaremesEcole();
  function resumeTarifs(motifId: string) {
    const m = (tousBaremes ?? []).filter((b) => b.motifId === motifId).map((b) => b.montant);
    if (m.length === 0) return null;
    const min = Math.min(...m);
    const max = Math.max(...m);
    return { min, max, nb: m.length };
  }
  const [editMotif, setEditMotif] = useState<EditMotifState | null>(null);
  // Motif visé par la pop-up de suppression (jamais payé uniquement — 409 sinon).
  const [suppressionMotif, setSuppressionMotif] = useState<EcoleMotif | null>(null);
  const deleteMutation = useDeleteEcoleMotif();

  function confirmerSuppression() {
    /* v8 ignore start -- défensif : confirmerSuppression n'est appelable que depuis le dialog rendu quand suppressionMotif est non nul */
    if (!suppressionMotif) return;
    /* v8 ignore stop */
    const libelle = suppressionMotif.libelle;
    deleteMutation.mutate(suppressionMotif.id, {
      onSuccess: () => {
        toast.success(`Motif « ${libelle} » supprimé`);
        setSuppressionMotif(null);
      },
      // 409 « déjà payé » : le message métier passe TEL QUEL (désactivez-le…).
      onError: (err: unknown) => toast.error(friendlyError(err)),
    });
  }

  function toggleActif(id: string, actif: boolean) {
    updateMutation.mutate({ motifId: id, data: { actif } }, {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getListEcoleMotifsQueryKey() });
        toast.success(actif ? "Motif activé" : "Motif désactivé");
      },
      onError: () => toast.error("Erreur"),
    });
  }

  function openEdit(m: EcoleMotif) {
    setEditMotif({
      id: m.id,
      libelle: m.libelle,
      categorie: m.categorie,
      frequence: m.periode ?? "",
      compteSyscohada: m.compteSyscohada ?? "",
      obligatoire: m.obligatoire === false ? "false" : "true",
    });
  }

  function submitEdit() {
    /* v8 ignore start -- défensif : submitEdit n'est appelable que depuis le dialog rendu quand editMotif est non nul */
    if (!editMotif) return;
    /* v8 ignore stop */
    if (!editMotif.libelle) {
      toast.error("Le libellé est obligatoire");
      return;
    }
    updateMutation.mutate(
      {
        motifId: editMotif.id,
        data: {
          libelle: editMotif.libelle,
          categorie: editMotif.categorie,
          frequence: editMotif.frequence || undefined,
          compteSyscohada: editMotif.compteSyscohada || undefined,
          obligatoire: editMotif.obligatoire === "true",
        },
      },
      {
        onSuccess: () => {
          qc.invalidateQueries({ queryKey: getListEcoleMotifsQueryKey() });
          toast.success("Motif modifié");
          setEditMotif(null);
        },
        onError: (err: unknown) => toast.error(friendlyError(err)),
      },
    );
  }

  return (
    <EcoleShell>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-navy">Motifs de paiement</h1>
          <p className="text-sm text-slate-600">
            Catalogue des frais payables dans votre école. Les montants se fixent par niveau dans « Tarifs »
          </p>
        </div>
        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button className="bg-navy hover:bg-navy/90 text-white" data-testid="button-create-motif" disabled={!isActiveAnnee}>
              <Plus className="w-4 h-4 mr-1.5" /> Nouveau motif
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader className="-mx-6 -mt-6 rounded-t-lg bg-navy px-6 py-4 text-left">
              <DialogTitle className="font-display text-lg font-bold text-white">Nouveau motif de paiement</DialogTitle>
              <DialogDescription className="text-white/70">
                Ajoutez une ligne payable au catalogue (scolarité, cantine…). Les montants se saisissent ensuite, niveau par niveau.
              </DialogDescription>
            </DialogHeader>
            <CreateMotifForm
              onDone={(created) => {
                setOpenCreate(false);
                qc.invalidateQueries({ queryKey: getListEcoleMotifsQueryKey() });
                // Le geste naturel suivant est de fixer les tarifs : on ouvre la grille.
                setBaremesOuverts(created.id);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      {!isActiveAnnee && <AnneeInactiveBanner />}

      <Card className="border border-slate-200 shadow-none overflow-hidden">
        {isLoading && <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-navy" /></div>}
        {motifs && (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Code</th>
                <th className="text-left">Libellé</th>
                <th className="text-left">Catégorie</th>
                <th className="text-right">Tarifs</th>
                <th className="text-left pl-3">Période</th>
                <th className="text-center">Actif</th>
                <th className="pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {motifs.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-500">Aucun motif. Cliquez sur Nouveau motif.</td></tr>}
              {motifs.map((m) => (
                <Fragment key={m.id}>
                <tr className={`border-t border-slate-100 ${m.actif ? "" : "bg-slate-50 text-slate-400"}`} data-testid={`row-motif-${m.code}`}>
                  <td className="px-4 py-2.5 font-mono text-xs text-navy" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                    {m.code}
                  </td>
                  <td className="font-medium">
                    {m.libelle}
                    {m.obligatoire === false && (
                      <span
                        className="ml-1.5 align-middle text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-700"
                        title="Facultatif : facturé seulement aux élèves pour qui il est activé (fiche élève)"
                        data-testid={`badge-facultatif-${m.code}`}
                      >
                        Facultatif
                      </span>
                    )}
                  </td>
                  <td><span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">{m.categorie}</span></td>
                  <td className="text-right font-mono text-xs" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                    {(() => {
                      const r = resumeTarifs(m.id);
                      if (!r) return <span className="text-amber-700 font-sans">Aucun tarif</span>;
                      return (
                        <span title={`${r.nb} classe(s) tarifée(s)`}>
                          {r.min === r.max ? fcfa(r.min) : `${fcfa(r.min)} à ${fcfa(r.max)}`}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="pl-3 text-slate-600 text-xs">{m.periode ?? "·"}</td>
                  <td className="text-center">
                    <Switch checked={m.actif} onCheckedChange={(v) => toggleActif(m.id, v)} disabled={!isActiveAnnee} data-testid={`switch-actif-${m.code}`} />
                  </td>
                  <td className="pr-4 text-right whitespace-nowrap">
                    <button
                      onClick={() => setBaremesOuverts(baremesOuverts === m.id ? null : m.id)}
                      className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1 mr-1.5"
                      data-testid={`button-toggle-baremes-${m.code}`}
                    >
                      {baremesOuverts === m.id ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      Tarifs
                    </button>
                    <button
                      onClick={() => openEdit(m)}
                      disabled={!isActiveAnnee}
                      className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`button-edit-${m.code}`}
                    >
                      <Pencil className="w-3 h-3" /> Modifier
                    </button>
                    <button
                      onClick={() => setSuppressionMotif(m)}
                      disabled={!isActiveAnnee}
                      className="text-xs px-2 py-1 ml-1.5 border border-red-200 text-red-600 rounded hover:bg-red-50 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Supprimer un motif créé par erreur (impossible s'il a déjà été payé)"
                      data-testid={`button-delete-${m.code}`}
                    >
                      <Trash2 className="w-3 h-3" /> Supprimer
                    </button>
                  </td>
                </tr>
                {baremesOuverts === m.id && (
                  <tr>
                    <td colSpan={7} className="p-0">
                      <MotifBaremesPanel motifId={m.id} motifLibelle={m.libelle} disabled={!isActiveAnnee} />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      <CsvImportCard
        title="Importer des tarifs par classe"
        description="Format des fiches SEDEC (onglet 04_Baremes) : classe,codeMotif,montant. La colonne classe accepte un libellé de classe (CM2A) ou de niveau (CM2, CP, 6e…). Un niveau s'applique à toutes ses classes. Le motif doit exister au catalogue."
        columns={["classe", "codeMotif", "montant"]}
        exampleCsv={`classe,codeMotif,montant
CP,SCOL,260000
CM1,SCOL,288000
CM2A,SCOL,317000
CP,CANT,45000`}
        importFn={(csv, dryRun) => importBaremesCsv(csv, dryRun)}
        filename="modele-tarifs.csv"
        invalidateQueries={[["motif-baremes"]]}
        disabled={!isActiveAnnee}
      />

      <Dialog
        open={!!editMotif}
        onOpenChange={(o) => {
          /* v8 ignore start -- défensif : sans DialogTrigger, Radix n'appelle onOpenChange qu'avec o=false (fermeture) ; la branche o=true est inatteignable */
          if (!o) setEditMotif(null);
          /* v8 ignore stop */
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader className="-mx-6 -mt-6 rounded-t-lg bg-navy px-6 py-4 text-left">
            <DialogTitle className="font-display text-lg font-bold text-white">Modifier le motif</DialogTitle>
            <DialogDescription className="text-white/70">
              Libellé, catégorie, compte et période. Les montants se modifient dans « Tarifs ».
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Libellé</Label>
              <Input value={editMotif?.libelle ?? ""} onChange={(e) => setEditMotif((p) => p && { ...p, libelle: e.target.value })} data-testid="edit-input-libelle" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Catégorie</Label>
                <select className="w-full border border-slate-300 rounded px-2 h-9 text-sm" value={editMotif?.categorie ?? "AUTRE"} onChange={(e) => setEditMotif((p) => p && { ...p, categorie: e.target.value })} data-testid="edit-select-categorie">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label>Compte SYSCOHADA (produit)</Label>
                <Input
                  value={editMotif?.compteSyscohada ?? ""}
                  onChange={(e) => setEditMotif((p) => p && { ...p, compteSyscohada: e.target.value })}
                  placeholder="7061"
                  maxLength={16}
                  data-testid="edit-input-compte"
                />
              </div>
            </div>
            <div>
              <Label>Période / fréquence</Label>
              <select className="w-full border border-slate-300 rounded px-2 h-9 text-sm" value={editMotif?.frequence ?? ""} onChange={(e) => setEditMotif((p) => p && { ...p, frequence: e.target.value })} data-testid="edit-select-frequence">
                {FREQUENCES.map((f) => <option key={f} value={f}>{f || "Non renseigné"}</option>)}
              </select>
              <p className="text-xs text-slate-400 mt-1">
                À titre indicatif : les dates réelles de paiement se fixent dans les tranches (bouton « Tarifs »).
              </p>
            </div>
            <div>
              <Label>Qui le doit ?</Label>
              <select
                className="w-full border border-slate-300 rounded px-2 h-9 text-sm"
                value={editMotif?.obligatoire ?? "true"}
                onChange={(e) => setEditMotif((p) => p && { ...p, obligatoire: e.target.value as "true" | "false" })}
                data-testid="edit-select-obligatoire"
              >
                <option value="true">Obligatoire : dû par tous les élèves</option>
                <option value="false">Facultatif : activé élève par élève (cantine, transport…)</option>
              </select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setEditMotif(null)}>Annuler</Button>
              <Button onClick={submitEdit} disabled={updateMutation.isPending} className="bg-navy text-white hover:bg-navy/90" data-testid="button-submit-edit">
                {updateMutation.isPending ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Suppression — motif jamais payé uniquement (409 sinon) */}
      <Dialog
        open={!!suppressionMotif}
        onOpenChange={(o) => {
          /* v8 ignore start -- défensif : sans DialogTrigger, Radix n'appelle onOpenChange qu'avec o=false (fermeture) ; la branche o=true est inatteignable */
          if (!o) setSuppressionMotif(null);
          /* v8 ignore stop */
        }}
      >
        <DialogContent className="max-w-sm" data-testid="dialog-delete-motif">
          <DialogHeader>
            <DialogTitle>Supprimer le motif</DialogTitle>
            <DialogDescription>
              Supprimer « {suppressionMotif?.libelle} » ? Ses tarifs et ses tranches seront
              supprimés avec lui. Un motif ayant déjà reçu des paiements ne peut pas être
              supprimé : désactivez-le à la place (switch « Actif »).
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setSuppressionMotif(null)} data-testid="button-cancel-delete-motif">
              Annuler
            </Button>
            <Button
              onClick={confirmerSuppression}
              disabled={deleteMutation.isPending}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="button-confirm-delete-motif"
            >
              {deleteMutation.isPending ? "Suppression…" : "Supprimer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </EcoleShell>
  );
}

function CreateMotifForm({ onDone }: { onDone: (created: EcoleMotif) => void }) {
  const createMutation = useCreateEcoleMotif();
  const { annees, anneeId } = useAnneeScope();
  const annee = annees.find((a) => a.id === anneeId);
  const [form, setForm] = useState({
    code: "",
    libelle: "",
    categorie: "AUTRE" as (typeof CATEGORIES)[number],
    compteSyscohada: "",
    periode: "",
    obligatoire: "true" as "true" | "false",
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!form.code || !form.libelle) {
          toast.error("Code et libellé sont obligatoires");
          return;
        }
        createMutation.mutate({ data: {
          code: form.code,
          libelle: form.libelle,
          categorie: form.categorie,
          frequence: form.periode || undefined,
          compteSyscohada: form.compteSyscohada || undefined,
          obligatoire: form.obligatoire === "true",
          // anneeScolaire omise → année scolaire courante de l'école (backend)
        }}, {
          onSuccess: (created) => {
            toast.success("Motif créé : fixez maintenant ses tarifs par niveau");
            onDone(created);
          },
          onError: (err: unknown) => toast.error(friendlyError(err)),
        });
      }}
      className="space-y-3"
    >
        <div className="rounded-lg bg-cream px-3 py-2 text-xs text-navy/70">
          Année scolaire : <span className="font-bold">{annee?.libelle ?? "·"}</span>
        </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Code</Label>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="SCOL" data-testid="input-code" />
        </div>
        <div>
          <Label>Catégorie</Label>
          <select className="w-full border border-slate-300 rounded px-2 h-9 text-sm" value={form.categorie} onChange={(e) => setForm({ ...form, categorie: e.target.value as any })}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div>
        <Label>Libellé</Label>
        <Input value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })} data-testid="input-libelle" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Compte SYSCOHADA (produit)</Label>
          <Input
            value={form.compteSyscohada}
            onChange={(e) => setForm({ ...form, compteSyscohada: e.target.value })}
            placeholder="7061"
            maxLength={16}
            data-testid="input-compte"
          />
        </div>
        <div>
          <Label>Période / fréquence</Label>
          <select className="w-full border border-slate-300 rounded px-2 h-9 text-sm" value={form.periode} onChange={(e) => setForm({ ...form, periode: e.target.value })}>
            <option value="">Non renseigné</option>
            <option value="T1">Trimestre 1</option>
            <option value="T2">Trimestre 2</option>
            <option value="T3">Trimestre 3</option>
            <option value="ANNUEL">Annuel</option>
            <option value="MENSUEL">Mensuel</option>
            <option value="INSCRIPTION">Inscription</option>
          </select>
          <p className="text-xs text-slate-400 mt-1">
            À titre indicatif : les dates réelles de paiement se fixent dans les tranches.
          </p>
        </div>
      </div>
      <div>
        <Label>Qui le doit ?</Label>
        <select
          className="w-full border border-slate-300 rounded px-2 h-9 text-sm"
          value={form.obligatoire}
          onChange={(e) => setForm({ ...form, obligatoire: e.target.value as "true" | "false" })}
          data-testid="input-obligatoire"
        >
          <option value="true">Obligatoire : dû par tous les élèves</option>
          <option value="false">Facultatif : activé élève par élève (cantine, transport…)</option>
        </select>
      </div>
      <Button type="submit" disabled={createMutation.isPending} className="w-full bg-navy hover:bg-navy/90 text-white">
        {createMutation.isPending ? "Création…" : "Créer le motif"}
      </Button>
    </form>
  );
}
